import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi, type BinderyRequestDetail } from "@/lib/api/bindery";
import { BinderyRequestStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { BinderyRequestActions } from "./request-detail/BinderyRequestActions";
import {
  BinderyRequestItemsTable,
  binderyQcPendingKey,
} from "./request-detail/BinderyRequestItemsTable";
import { formatDate, formatMoney } from "./binderyFormatters";

export default function BinderyRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [req, setReq] = useState<BinderyRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setReq(await binderyApi.requests.getById(id));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to load request"));
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = async (key: string, label: string, fn: () => Promise<BinderyRequestDetail>) => {
    setPendingKey(key);
    try {
      const next = await fn();
      setReq(next);
      toast.success(label);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Failed to ${label.toLowerCase()}`));
    } finally {
      setPendingKey(null);
    }
  };

  if (loading || !req) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={req.requestNumber}
        description={`${req.vendor.name} · ${req.items.length} item(s)`}
        breadcrumbItems={[
          { label: "Bindery requests", href: "/bindery/requests" },
          { label: req.requestNumber },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          <BinderyRequestActions
            status={req.status}
            pendingKey={pendingKey}
            onAction={(action) => {
              if (action === "send") {
                void run("send", "Request sent", () => binderyApi.requests.send(req.id, {}));
              }
              if (action === "cancel") {
                void run("cancel", "Request cancelled", () => binderyApi.requests.cancel(req.id));
              }
              if (action === "repair") {
                void run("repair", "Marked in repair", () =>
                  binderyApi.requests.markInRepair(req.id)
                );
              }
              if (action === "receive") {
                void run("receive", "Batch received", () =>
                  binderyApi.requests.receiveBatch(req.id)
                );
              }
            }}
          />
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <InfoTile label="Status" value={<BinderyRequestStatusBadge status={req.status} />} />
        <InfoTile label="Vendor" value={req.vendor.name} />
        <InfoTile label="Sent" value={formatDate(req.sentAt)} />
        <InfoTile label="Expected back" value={formatDate(req.expectedReturnAt)} />
        <InfoTile label="Received" value={formatDate(req.receivedAt)} />
        <InfoTile label="Shipping ref" value={req.shippingReference ?? "—"} />
        <InfoTile
          label="Created by"
          value={
            [req.createdBy.firstName, req.createdBy.lastName].filter(Boolean).join(" ") ||
            req.createdBy.email
          }
        />
        <InfoTile label="Total cost" value={formatMoney(req.costTotal)} />
      </div>

      {req.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{req.notes}</p>
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Items</h3>
        <BinderyRequestItemsTable
          items={req.items}
          requestStatus={req.status}
          pendingKey={pendingKey}
          onQc={(itemRowId, outcome) =>
            run(binderyQcPendingKey(itemRowId, outcome), "QC recorded", () =>
              binderyApi.requests.qcItem(req.id, itemRowId, { qcOutcome: outcome })
            )
          }
        />
      </div>
    </PageContainer>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}
